import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Placeholder from "@/v2-ui/Placeholder"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { useLayerQuery } from "@/v2-backoffice-client/layer/layer.query"
import LayerUptimeCell from "@/v2-backoffice-client/layer/LayerUptimeCell"
import { LayerUptimeMapProvider } from "@/v2-backoffice-client/layer/LayerUptimeMapContext"
import LayerDriftCell from "@/v2-backoffice-client/layer/drift/LayerDriftCell"
import { LayerDriftMapProvider } from "@/v2-backoffice-client/layer/drift/LayerDriftMapContext"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import LayerReviewPublishButton from "@/v2-backoffice-client/layer/review/LayerReviewPublishButton"
import LayerReviewRejectButton from "@/v2-backoffice-client/layer/review/LayerReviewRejectButton"
import LayerConsolePreviewButton from "@/v2-backoffice-client/layer/review/LayerConsolePreviewButton"
import { AGOL_SEARCH_BASE_URL } from "@/v2-backoffice-client/layer/layer.constants"
import {
  LAYER_SEARCH_COLUMN,
  LAYER_SEARCH_COLUMN_ID,
  LAYER_SEARCH_PLACEHOLDER,
} from "@/v2-backoffice-client/layer/layer.columns"

const COLUMN_SIZING = {
  CreatedAt: 130,
  Uptime: 200,
  Drift: 120,
  Review: 290,
}

const columns = createColumns<LayerFragment>({
  enableSelection: true,
  enableSorting: true,
  columns: {
    Name: {
      id: "Name",
      label: "Name",
      to: ({ row }) => `?v=layer:${row.original.FeatureCode}`,
    },
    FeatureCode: {
      id: "FeatureCode",
      label: "Feature Code",
      filter: { type: "text", placeholder: "Filter by code..." },
      to: ({ value }) => `${AGOL_SEARCH_BASE_URL}${value}`,
    },
    [LAYER_SEARCH_COLUMN_ID]: LAYER_SEARCH_COLUMN,
    LayerTypeCode: {
      id: "LayerTypeCode",
      label: "Type",
      enableGrouping: true,
    },
    CreatedAt: {
      id: "CreatedAt",
      label: "Seeded",
      renderCell({ row }) {
        if (!row.original.CreatedAt) return null
        return formatDateString(String(row.original.CreatedAt))
      },
    },
    // Uptime + Drift are joined client-side via the shared lookup
    // providers (see LayerTable) so a reviewer sees upstream health
    // before deciding to publish.
    Uptime: {
      id: "Uptime",
      label: "Uptime",
      enableSorting: false,
      renderCell({ row }) {
        return <LayerUptimeCell featureCode={row.original.FeatureCode} />
      },
    },
    Drift: {
      id: "Drift",
      label: "Drift",
      enableSorting: false,
      renderCell({ row }) {
        return <LayerDriftCell featureCode={row.original.FeatureCode} />
      },
    },
    Review: {
      id: "Review",
      label: "Review",
      // Action buttons only; there's no row value to sort on.
      enableSorting: false,
      renderCell({ row }) {
        return (
          <span className="layer-review-table-actions flex items-center gap-2">
            <LayerConsolePreviewButton layer={row.original} />
            <LayerReviewPublishButton layers={[row.original]} />
            <LayerReviewRejectButton layers={[row.original]} />
          </span>
        )
      },
    },
  },
})

export type LayerReviewTableProps = Omit<
  TableProps<LayerFragment>,
  "data" | "columns"
>

/**
 * The review queue: layers that arrived via seeding and have never been
 * through a backoffice review. Click the name to inspect/adjust the layer in
 * the view sheet, then publish it to customers or reject it. Both decisions
 * stamp the reviewer and drop the layer from this list.
 */
function LayerReviewTable(props: LayerReviewTableProps) {
  const { className, ...tableProps } = props

  const opener = useAppViewSheetOpener()

  const { data, previousData, loading, error, refetch } = useLayerQuery({
    notifyOnNetworkStatusChange: true,
    // Pending review = disabled AND never reviewed: an enabled layer is live
    // and therefore de facto approved, even if it predates the review queue.
    // (Same predicate as backoffice.Get_Gis_LayerCounts' PendingReview.)
    variables: { input: { isReviewed: false, isEnabled: false } },
  })

  const displayData = data ?? previousData
  // Also filter client-side so a just-reviewed layer drops out immediately
  // from the normalized cache update, without waiting for a refetch.
  const layers = (displayData?.getLayers ?? []).filter(
    (layer) => !layer.ReviewedAt && !layer.IsEnabled,
  )

  return (
    <LayerUptimeMapProvider>
      <LayerDriftMapProvider>
        <Table
          className={cn("layer-review-table", className)}
          tableId="layer-review-table"
          data={layers}
          columns={columns}
          initialColumnSizing={COLUMN_SIZING}
          enableRowSelection
          getHighlightedRowId={(row) => {
            return opener.getIsActive("layer", String(row.original.FeatureCode))
          }}
          {...tableProps}
        >
          <Table.Toolbar>
            <p className="text-sm text-muted-foreground">
              Newly seeded layers land here hidden from customers. Open a layer
              to inspect and adjust it, then take it live &mdash; or reject it
              with a note. Publishing the catalog never changes this list: it
              ships definitions, Go live shows them.
            </p>

            <div className="flex-1" />

            <Table.Search
              column={LAYER_SEARCH_COLUMN_ID}
              placeholder={LAYER_SEARCH_PLACEHOLDER}
            />
            <Separator orientation="vertical" />
            <Table.ToolbarMenu
              onRefresh={refetch}
              exportFileName="layers-review"
            />
          </Table.Toolbar>

          <Table.FilterList />

          <Table.Content
            isLoading={loading}
            error={error}
            renderEmpty={
              <Placeholder>
                No layers waiting for review &mdash; all caught up.
              </Placeholder>
            }
          />

          <Table.SelectionToolbar<LayerFragment>>
            {(selectedLayers, resetSelection) => (
              <>
                <LayerReviewPublishButton
                  variant="ghost"
                  isStacked
                  layers={selectedLayers}
                  onDone={resetSelection}
                />
                <LayerReviewRejectButton
                  variant="ghost"
                  isStacked
                  layers={selectedLayers}
                  onDone={resetSelection}
                />
              </>
            )}
          </Table.SelectionToolbar>
        </Table>
      </LayerDriftMapProvider>
    </LayerUptimeMapProvider>
  )
}

export default LayerReviewTable
